<!-- eslint-disable vuejs-accessibility/label-has-for -->
<template>
  <div
    :class="classes"
    :style="styles"
  >
    <div
      class="input-container accessibility-outline outline-inset"
      :aria-disabled="disabled"
      :aria-required="isRequired"
      :aria-readonly="isReadOnly"
      :aria-invalid="isAriaInvalid"
      :aria-label="ariaLabel || sanitizedLabel || 'Auswahl'"
      :aria-placeholder="placeholderText"
      :class="{ 'round-top': roundTop, 'round-bottom': roundBottom }"
    >
      <div class="input-container-inner">
        <div
          v-if="iconLeft"
          class="icon-left"
        >
          <BaseIcon :icon="iconLeft" />
        </div>
        <ClickWrapper
          v-if="!!prefixes || currentPrefix"
          class="prefix"
          :class="{ open: showPrefixes }"
          @outside="showPrefixes = false"
        >
          <div
            class="prefix__selected accessibility-outline"
            :class="{ 'cursor-pointer': !!prefixes }"
            :role="prefixes && prefixes.length > 1 && !disabled ? 'button' : undefined"
            :tabindex="prefixes && prefixes.length > 1 && !disabled ? 0 : -1"
            :aria-label="ariaLabel || prefixButtonLabel"
            :aria-expanded="prefixes && prefixes.length > 1 ? (showPrefixes ? 'true' : 'false') : undefined"
            :aria-controls="prefixes && prefixes.length > 1 ? prefixOptionsId : undefined"
            :aria-disabled="disabled"
            @click="openPrefixes"
            @keydown.enter.prevent="openPrefixes"
            @keydown.space.prevent="openPrefixes"
          >
            <div>{{ getKeyOrObject(currentPrefix, 'label') }}</div>
            <div v-if="!!prefixes && prefixes.length > 1">
              <i class="fa-solid fa-chevron-down" />
            </div>
          </div>
          <div
            v-if="!!prefixes && prefixes.length > 1"
            :id="prefixOptionsId"
            class="prefix__options"
            role="listbox"
            :aria-hidden="showPrefixes ? 'false' : 'true'"
          >
            <div
              v-for="(prefix, inx) in prefixes"
              :key="inx"
              class="accessibility-outline"
              :class="{ selected: getKeyOrObject(prefix, 'value') === getKeyOrObject(currentPrefix, 'value') }"
              role="option"
              :aria-selected="getKeyOrObject(prefix, 'value') === getKeyOrObject(currentPrefix, 'value') ? 'true' : 'false'"
              tabindex="0"
              :tabindex="showPrefixes ? 0 : -1"
              @click="selectPrefix(prefix)"
              @keydown.enter.prevent="selectPrefix(prefix)"
              @keydown.space.prevent="selectPrefix(prefix)"
            >
              {{ getKeyOrObject(prefix, 'label') }}
            </div>
          </div>
        </ClickWrapper>
        <div
          class="input-field"
          @click="$refs.input.focus()"
          @keydown.enter="$refs.input.focus()"
        >
          <span
            v-if="placeholder && !hasValue"
            class="placeholder"
          >{{ placeholder }}</span>
          <label
            v-if="label"
            :for="baseFieldId"
          >{{ label }}</label>
          <input
            :id="baseFieldId"
            ref="input"
            v-model="inputValue"
            class="accessibility-outline"
            :name="name"
            :type="internalType"
            :min="min"
            :max="max"
            :required="isRequired"
            :readonly="isReadOnly"
            :disabled="disabled"
            :autocomplete="autocomplete"
            :minlength="minLength"
            :maxlength="maxLength"
            :step="inputStep"
            :aria-label="ariaLabel || sanitizedLabel"
            :aria-describedby="fieldAriaDescribedby || undefined"
            :aria-invalid="isAriaInvalid ? 'true' : 'false'"
            :aria-required="isRequired"
            :aria-readonly="isReadOnly"
            :aria-disabled="disabled"
            @paste="onPaste($event)"
            @drop="onDrop($event)"
            @keydown="onKeyDown($event)"
            @focusin="onFocusIn($event)"
            @focusout="[focusOut(), onFocusOut($event)]"
          >
          <span
            v-if="inputSuffix && hasValue"
            class="input-suffix"
          ><span>{{ inputSuffix }}</span></span>
        </div>
        <div
          v-if="!units && (isPassword || hasValue || getIconRight)"
          class="icon-right"
          :class="{ 'accessibility-outline': iconRightInteractive }"
          :role="iconRightInteractive ? 'button' : undefined"
          :tabindex="iconRightInteractive && !disabled ? 0 : -1"
          :aria-label="iconRightLabel || undefined"
          :aria-disabled="disabled"
          @click="isPassword ? switchPasswordReadability() : hasValue && isClearable ? clear($event, { val: '' }) : ''"
          @keydown.enter.prevent="isPassword ? switchPasswordReadability() : hasValue && isClearable ? clear($event, { val: '' }) : ''"
          @keydown.space.prevent="isPassword ? switchPasswordReadability() : hasValue && isClearable ? clear($event, { val: '' }) : ''"
        >
          <BaseIcon
            v-if="isPassword && hasValue && !showPassword"
            icon="fa-light fa-eye-slash"
          />
          <BaseIcon
            v-else-if="isPassword && hasValue && showPassword"
            icon="fa-light fa-eye"
          />
          <BaseIcon
            v-else-if="hasValue && isClearable"
            icon="fa-light fa-xmark"
          />
          <BaseIcon
            v-else
            icon="getIconRight"
          />
        </div>
        <ClickWrapper
          v-if="!!units || currentUnit"
          class="unit"
          :class="{ open: showUnits }"
          @outside="showUnits = false"
        >
          <div
            class="unit__selected accessibility-outline"
            :class="{ 'cursor-pointer': !!units }"
            :role="units && units.length > 1 && !disabled ? 'button' : undefined"
            :tabindex="units && units.length > 1 && !disabled ? 0 : -1"
            :aria-label="ariaLabel || unitButtonLabel"
            :aria-expanded="units && units.length > 1 ? (showUnits ? 'true' : 'false') : undefined"
            :aria-controls="units && units.length > 1 ? unitOptionsId : undefined"
            :aria-disabled="disabled"
            @click="openUnits"
            @keydown.enter.prevent="openUnits"
            @keydown.space.prevent="openUnits"
          >
            <div>{{ getKeyOrObject(currentUnit, 'label') }}</div>
            <div v-if="!!units && units.length > 1">
              <i class="fa-solid fa-chevron-down" />
            </div>
          </div>
          <div
            v-if="!!units && units.length > 1"
            :id="unitOptionsId"
            class="unit__options"
            role="listbox"
            :aria-hidden="showUnits ? 'false' : 'true'"
          >
            <div
              v-for="(unitValue, inx) in units"
              :key="inx"
              class="accessibility-outline"
              :class="{ selected: getKeyOrObject(unitValue, 'value') === getKeyOrObject(currentUnit, 'value') }"
              role="option"
              :aria-selected="getKeyOrObject(unitValue, 'value') === getKeyOrObject(currentUnit, 'value') ? 'true' : 'false'"
              tabindex="0"
              :tabindex="showUnits ? 0 : -1"
              @click="selectUnit(unitValue)"
              @keydown.enter.prevent="selectUnit(unitValue)"
              @keydown.space.prevent="selectUnit(unitValue)"
            >
              {{ getKeyOrObject(unitValue, 'label') }}
            </div>
          </div>
        </ClickWrapper>
      </div>
    </div>
    <div
      v-if="hasError || hint || counter"
      class="input-hint"
    >
      <div
        v-if="hasError"
        :id="errorMessageId"
        class="input-hint--error"
        role="alert"
        aria-live="assertive"
        v-html="errorMessage"
      />
      <div
        v-if="hint && !hasError"
        :id="hintId"
        class="input-hint--hint"
        aria-live="polite"
      >
        <span>{{ hint }}</span>
      </div>
      <div
        v-if="counter"
        :id="counterId"
        class="input-hint--counter"
      >
        <span>{{ value.length }} {{ maxLength > 0 ? ` | ${maxLength}` : '' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import BaseInputMixin from '../../../shared/mixins/BaseInputMixin';
import ClickWrapper from '../../ClickWrapper.vue';

const VALID_TYPES = ['text', 'number', 'email', 'phone', 'password', 'hidden', 'tel', 'percentage', 'currency', 'search'];

export default {
  name: 'BaseInput',

  components: { ClickWrapper },

  mixins: [
    BaseInputMixin,
  ],

  props: {
    value: {
      type: [String, Number, Boolean],
      default: '',
    },
    type: {
      type: String,
      default: 'text',
      validator: (value) => VALID_TYPES.includes(value),
    },
    minLength: {
      type: [Number, String],
      default: null,
    },
    maxLength: {
      type: [Number, String],
      default: null,
    },
    maxDecimals: {
      type: Number,
      default: 0,
    },
    step: {
      type: [Number, String],
      default: 1,
    },
    unit: {
      type: [Number, String, Boolean],
      default: null,
    },
    units: {
      type: Array,
      default: null,
    },
    prefix: {
      type: [Number, String],
      default: null,
    },
    prefixes: {
      type: Array,
      default: null,
    },
    onlyNumeric: {
      type: Boolean,
      default: false,
    },
    minIfEmpty: {
      type: Boolean,
      default: true,
    },
    roundTop: {
      type: Boolean,
      default: false,
    },
    roundBottom: {
      type: Boolean,
      default: false,
    },
    inputSuffix: {
      type: String,
      default: null,
    },
    transformInput: {
      type: Function,
      default: (v) => v,
    },
    ariaLabel: {
      type: String,
      default: '',
    },
  },

  emits: ['updateUnit', 'updatePrefix', 'update'],
  data() {
    const isNumber = this.type === 'number';
    const isPercentage = this.type === 'percentage';
    const isSearch = this.type === 'search';

    let { type } = this;
    if (isNumber) {
      type = 'text';
    } else if (isPercentage) {
      type = 'number';
    } else if (isSearch) {
      type = 'text';
    }

    return {
      internalType: type,
      showPassword: false,
      showUnits: false,
      showPrefixes: false,
      safeFloats: [],
      inputFieldCenteredPadding: 0,
      internalUnit: this.units?.find((unit) => this.getKeyOrObject(unit, 'value') === this.unit),
      internalPrefix: this.prefixes?.find((prefix) => this.getKeyOrObject(prefix, 'value') === this.prefix),
    };
  },
  computed: {
    isPassword() {
      return this.type === 'password';
    },
    isNumber() {
      return this.type === 'number';
    },
    isHidden() {
      return this.type === 'hidden';
    },
    isPercentage() {
      return this.type === 'percentage';
    },
    isSearch() {
      return this.type === 'search';
    },
    suffixPosition() {
      return `${String(this.value).length * 9.6 + (1.5 * 18)}px`;
    },
    currentUnit: {
      get() {
        return this.internalUnit;
      },
      set(unit) {
        this.internalUnit = unit;
        this.$emit('updateUnit', this.getKeyOrObject(unit, 'value'));
      },
    },
    currentPrefix: {
      get() {
        return this.internalPrefix;
      },
      set(prefix) {
        this.internalPrefix = prefix;
        this.$emit('updatePrefix', this.getKeyOrObject(prefix, 'value'));
      },
    },
    prefixOptionsId() {
      return `${this.uniqueId}-prefix-options`;
    },
    unitOptionsId() {
      return `${this.uniqueId}-unit-options`;
    },
    prefixButtonLabel() {
      const label = this.getKeyOrObject(this.currentPrefix, 'label') || '';
      const base = label ? `Präfix ${label}` : 'Präfix auswählen';
      if (!this.prefixes || this.prefixes.length <= 1 || this.disabled) {
        return base;
      }

      return this.showPrefixes ? `${base} schließen` : `${base} öffnen`;
    },
    unitButtonLabel() {
      const label = this.getKeyOrObject(this.currentUnit, 'label') || '';
      const base = label ? `Einheit ${label}` : 'Einheit auswählen';
      if (!this.units || this.units.length <= 1 || this.disabled) {
        return base;
      }

      return this.showUnits ? `${base} schließen` : `${base} öffnen`;
    },
    iconRightInteractive() {
      return (!this.disabled && (
        (this.isPassword && this.hasValue)
        || (this.hasValue && this.isClearable)
      ));
    },
    iconRightLabel() {
      if (!this.iconRightInteractive) {
        return null;
      }
      if (this.isPassword && this.hasValue) {
        return this.showPassword ? 'Passwort verbergen' : 'Passwort anzeigen';
      }
      if (this.hasValue && this.isClearable) {
        return 'Eingabe löschen';
      }

      return null;
    },
    inputValue: {
      get() {
        if (this.isNumber) {
          return `${typeof this.value === 'number' ? this.value : (this.value || '')}`.replace('.', this.commaSeparator);
        }

        return this.value;
      },
      set(value) {
        const processedValue = this.transformInput(value);
        const validatedValue = this.getValidatedValue(processedValue).value;

        this.$emit('input', validatedValue);
        this.handleEmits(validatedValue);
      },
    },
    inputStep() {
      return this.isNumber
        ? (1 / 10 ** this.maxDecimals)
        : null;
    },
  },
  mounted() {
    if (this.minIfEmpty && this.isNumber && this.min && (!this.hasValue || this.value < this.min)) {
      this.emitUpdate(this.min);
    }

    if (!!this.units && !this.unit) {
      const firstUnit = this.getKeyOrObject(this.units[0], 'value');

      if (firstUnit) {
        this.selectUnit(firstUnit);
      }
    }

    if (!!this.prefixes && !this.prefix) {
      const firstPrefix = this.getKeyOrObject(this.prefixes[0], 'value');

      if (firstPrefix) {
        this.selectPrefix(firstPrefix);
      }
    }

    let paddingTopVar = '--base-input--padding-top';
    let paddingBottomVar = '--base-input--padding-bottom';
    if (this.xSmall) {
      paddingTopVar = '--base-input--x-small-padding-top';
      paddingBottomVar = '--base-input--x-small-padding-bottom';
    } else if (this.small) {
      paddingTopVar = '--base-input--small-padding-top';
      paddingBottomVar = '--base-input--small-padding-bottom';
    }

    const paddingTop = parseFloat(window.getComputedStyle(document.body).getPropertyValue(paddingTopVar));
    const paddingBottom = parseFloat(window.getComputedStyle(document.body).getPropertyValue(paddingBottomVar));

    this.inputFieldCenteredPadding = `${(paddingTop + paddingBottom) / 2}em`;
  },
  methods: {
    getNumber(value) {
      const unit = value.match(/[a-z]+/);
      const result = unit ? Number(value.replace(unit[0], '')) : Number(value);

      if (this.small) return result * 0.875;
      if (this.xSmall) return result * 0.75;

      return result;
    },
    selectUnit(unit) {
      this.currentUnit = unit;
      this.showUnits = false;
    },
    selectPrefix(prefix) {
      this.currentPrefix = prefix;
      this.showPrefixes = false;
    },
    openUnits() {
      if (this.disabled) {
        return;
      }
      if (this.units && this.units.length > 1) {
        this.showUnits = !this.showUnits;
      }
    },
    getKeyOrObject(obj, key) {
      return typeof obj === 'object' ? obj?.[key] : obj;
    },
    openPrefixes() {
      if (this.disabled) {
        return;
      }
      if (this.prefixes && this.prefixes.length > 1) {
        this.showPrefixes = !this.showPrefixes;
      }
    },
    switchPasswordReadability() {
      this.showPassword = !this.showPassword;
      if (this.showPassword) {
        this.internalType = 'text';
      } else {
        this.internalType = 'password';
      }
    },
    focusOut() {
      if (this.isNumber && !Number.isNaN(parseInt(this.value, 10)) && typeof parseInt(this.value, 10) === 'number') {
        if (this.min && this.value < this.min) {
          this.emitUpdate(this.min);
        }
        if (this.max && this.value > this.max) {
          this.emitUpdate(this.max);
        }
      } else if (this.isNumber && Number.isNaN(parseInt(this.value, 10))) {
        this.emitUpdate(null);
      }
    },
    ClassesHook() {
      return {
        [`${this.classDefault}--${this.type}`]: this.type,
        [`${this.classDefault}--min-length`]: this.minLength !== null,
        [`${this.classDefault}--max-length`]: this.maxLength !== null,
        [`${this.classDefault}--step-${this.step}`]: this.step !== null,
        'units-open': this.showUnits,
        'prefixes-open': this.showPrefixes,
      };
    },
    KeyDownHook({ event, eventType }) {
      if (this.type === 'number') {
        const isThousandSeparator = this.keyboardEventHelper.isKey(event, this.thousandSeparator);
        const isCommaSeparator = this.keyboardEventHelper.isKey(event, this.commaSeparator);
        if (isThousandSeparator || (isCommaSeparator && this.maxDecimals === 0)) {
          event.preventDefault();
          return;
        }

        const isPaste = eventType === 'paste';
        const isDrop = eventType === 'drop';
        const isArrowUp = this.keyboardEventHelper.isArrowUp(event);
        const isArrowDown = this.keyboardEventHelper.isArrowDown(event);
        const isTab = this.keyboardEventHelper.isTab(event);
        const isCtrl = !!event.ctrlKey;
        const isShift = !!event.shiftKey;

        if (!isTab && (isPaste || isDrop || !isCtrl || isArrowUp || isArrowDown || isCommaSeparator)) {
          if (!(isPaste || isDrop) && !this.keyboardEventHelper.isNumberKey(event)) {
            event.preventDefault();
            return;
          }

          let valueToValidate = null;

          if (isPaste && (event.clipboardData || window.clipboardData)) {
            valueToValidate = (event.clipboardData || window.clipboardData).getData('text');
          } else if (isDrop && event.dataTransfer) {
            valueToValidate = event.dataTransfer?.getData('text');
          } else if (this.keyboardEventHelper.isNumberKey(event)) {
            const currentKeyNumber = this.keyboardEventHelper.getKeyNumber(event);
            let numberToAdd = '';
            if (typeof currentKeyNumber === 'number') {
              numberToAdd = currentKeyNumber;
            } else if (isCommaSeparator) {
              numberToAdd = this.commaSeparator;
            }
            const placeNumberAt = event.target.selectionStart;
            const valueToValidateParts = [
              `${event.target.value}`.substring(0, placeNumberAt),
              numberToAdd,
              `${event.target.value}`.substring(placeNumberAt),
            ];

            valueToValidate = valueToValidateParts.join('');
          }

          const validatedValue = this.getValidatedValue(valueToValidate, (isPaste || isDrop));

          if (validatedValue.numberLength > 14) {
            event.preventDefault();
            return;
          }

          if (validatedValue.value > Number.MAX_SAFE_INTEGER) {
            event.preventDefault();
            return;
          }

          if (validatedValue.commasCount >= 2) {
            event.preventDefault();
            return;
          }

          if (this.maxDecimals && validatedValue.decimals > this.maxDecimals) {
            event.preventDefault();
            return;
          }

          if (!validatedValue.value && !(this.keyboardEventHelper.isNumberKey(event))) {
            event.preventDefault();
            return;
          }

          let valueToEmit = validatedValue.value;
          let step = this.step ? parseFloat(this.step) : 1;

          let minStep = 1;
          if (this.step) {
            minStep = 1 / 10 ** this.maxDecimals;

            if (step < minStep) {
              step = minStep;
            }
          }

          if (isCtrl) {
            if (step / 10 >= minStep) {
              step /= 10;
            }
          } else if (isShift) {
            step *= 10;
          }

          if (isArrowUp || isArrowDown) {
            if (isArrowUp) {
              valueToEmit = parseFloat(valueToEmit || 0) + step;
            } else if (isArrowDown) {
              valueToEmit = parseFloat(valueToEmit || 0) - step;

              if (valueToEmit < 0) {
                valueToEmit = 0;
              }
            }

            const stepDecimals = `${step}`.split('.')[1]?.length || 0;

            valueToEmit = `${(Math.round(valueToEmit * 1e12) / 1e12).toFixed(stepDecimals > validatedValue.decimals ? stepDecimals : validatedValue.decimals)}`;
          }

          if (isPaste || isDrop || isArrowUp || isArrowDown) {
            event.preventDefault();
            this.$emit('input', valueToEmit);
            this.emitUpdate(valueToEmit);
          }
        }
      } else if (this.onlyNumeric && !this.keyboardEventHelper.isNumeric(event)) {
        event.preventDefault();
      }
    },
    onPaste(event) {
      this.KeyDownHook({ event, eventType: 'paste' });
    },
    onDrop(event) {
      this.KeyDownHook({ event, eventType: 'drop' });
    },
  },
};
</script>

<style lang="scss" scoped>
.input-field {
  position: relative;
  padding-top: v-bind(inputFieldCenteredPadding);
  padding-bottom: v-bind(inputFieldCenteredPadding);
}
.input-container-inner {
  display: grid;
  grid-template-columns: v-bind(gridTemplateColumns);
}
.description {
  padding: 0 1.5rem;
}
.input-suffix {
  position: absolute;
  left: v-bind(suffixPosition);
  height: var(--base-input--input-height);
  display: flex;
  align-items: center;
  pointer-events: none;
}
.placeholder {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  line-height: 1;
  background-color: transparent;
  cursor: text;
}
.round-top {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: 0;
}
.round-bottom {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
</style>
